import { useState } from 'react';
import { useVisitors, useVisitor } from '../api';
import { useScope } from '../../store/scope';
import { PageLoader, ErrorState, EmptyState, Pagination, StatusBadge } from '../../components/ui';
import { Modal } from '../../components/Modal';
import { dateTime, money, num } from '../../lib/format';

export function VisitorsPage() {
  const { participationId } = useScope();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [contactOnly, setContactOnly] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useVisitors({
    page,
    participationId: participationId || undefined,
    search: search || undefined,
    hasContactDetails: contactOnly ? true : undefined,
  });

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">Visitors</h1>
        <p className="text-sm text-ink-400">
          {data?.meta ? `${num(data.meta.total)} visitor sessions` : ' '}
          {participationId ? ' · this expo' : ' · all expos'}
        </p>
      </div>

      <div className="card flex flex-wrap items-center gap-2 p-3">
        <input
          className="input max-w-xs"
          placeholder="Name / mobile / email / company…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input
            type="checkbox"
            checked={contactOnly}
            onChange={(e) => {
              setPage(1);
              setContactOnly(e.target.checked);
            }}
          />
          With contact details only
        </label>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No visitors yet"
          hint="A visitor record is created when someone scans a rack QR. Contact details fill in when they book."
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">Visitor</th>
                  <th className="px-3 py-2">Mobile</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2 text-right">Bookings</th>
                  <th className="px-3 py-2">First seen</th>
                  <th className="px-3 py-2">Last seen</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((v) => (
                  <tr key={v._id} className="cursor-pointer hover:bg-ink-50/60" onClick={() => setDetailId(v._id)}>
                    <td className="px-3 py-2">
                      <span className="font-medium">{v.name || 'Anonymous'}</span>
                      {v.email && <span className="block text-xs text-ink-400">{v.email}</span>}
                    </td>
                    <td className="px-3 py-2">{v.mobile || '—'}</td>
                    <td className="px-3 py-2 text-ink-600">{v.companyName || '—'}</td>
                    <td className="px-3 py-2 text-right">{v.bookingCount}</td>
                    <td className="px-3 py-2 text-xs text-ink-400">{dateTime(v.firstSeenAt)}</td>
                    <td className="px-3 py-2 text-xs text-ink-400">{dateTime(v.lastSeenAt)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="btn-ghost px-2 py-1 text-xs">Open</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3">
            <Pagination page={page} pages={data?.meta?.pages ?? 1} onPage={setPage} />
          </div>
        </div>
      )}

      <VisitorDetailModal id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function VisitorDetailModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useVisitor(id);
  return (
    <Modal open={!!id} onClose={onClose} title={data?.name || 'Visitor'} size="lg">
      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} />
      ) : data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Info label="Mobile" value={data.mobile} />
            <Info label="Email" value={data.email} />
            <Info label="Company" value={data.companyName} />
            <Info label="Expo" value={data.participationName} />
            <Info label="First seen" value={dateTime(data.firstSeenAt)} />
            <Info label="Last seen" value={dateTime(data.lastSeenAt)} />
            <Info label="QR scans" value={num(data.activity.scans)} />
            <Info label="Product views" value={num(data.activity.productViews)} />
            <Info label="Session" value={data.sessionId} mono />
          </div>

          {data.notes && (
            <div>
              <p className="label mb-1">Notes</p>
              <p className="rounded-lg bg-ink-50 p-3 text-sm text-ink-700">{data.notes}</p>
            </div>
          )}

          <div>
            <p className="label mb-2">Bookings ({data.bookings.length})</p>
            {data.bookings.length === 0 ? (
              <p className="text-sm text-ink-400">No bookings from this visitor.</p>
            ) : (
              <div className="divide-y divide-ink-50 rounded-lg border border-ink-100">
                {data.bookings.map((b) => (
                  <div key={b._id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-mono text-xs">{b.bookingNumber}</span>
                    <span className="text-xs text-ink-400">{dateTime(b.createdAt)}</span>
                    <span>{b.totalQuantity} pcs</span>
                    <span className="font-medium">{money(b.totalAmount)}</span>
                    <StatusBadge status={b.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function Info({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <p className="label">{label}</p>
      <p className={`text-sm text-ink-800 ${mono ? 'break-all font-mono text-xs' : ''}`}>{value || '—'}</p>
    </div>
  );
}
